import React, { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSeason } from '@/contexts/SeasonContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

// Same plausibility bounds the server applies (workers/api/src/lib/season.ts).
const MIN_SEASON_YEAR = 1900;
const MAX_SEASON_YEAR = 2999;
const ALL_SEASONS = 'all';

const toSeasonYear = (value) => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= MIN_SEASON_YEAR && n <= MAX_SEASON_YEAR ? n : null;
};

/** Accept a year or the literal "all"; junk values become empty (no selection yet). */
const seasonParamValue = (raw) => {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (s.toLowerCase() === ALL_SEASONS) return ALL_SEASONS;
  const year = toSeasonYear(s);
  return year === null ? '' : String(year);
};

export default function AdminUsers({ token, me, setMe }) {
  const season = useSeason();
  const [searchParams, setSearchParams] = useSearchParams();
  // Sort direction for users list: 'asc' | 'desc'
  const [userSortDir, setUserSortDir] = useState('desc');
  // Simple CSV value escaper
  const csvVal = (v) => {
    const s = v == null ? '' : String(v);
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  // Format a Date or ISO string to input[type="datetime-local"] value in LOCAL time (YYYY-MM-DDTHH:mm)
  const formatForDatetimeLocal = (input) => {
    try {
      const d = (input instanceof Date) ? input : new Date(input);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      const yyyy = d.getFullYear();
      const MM = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mm = pad(d.getMinutes());
      return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
    } catch { return ''; }
  };
  const normalizePhone = (s) => {
    if (typeof s !== 'string') return '';
    let t = s.trim();
    if (!t) return '';
    const hasPlus = t.startsWith('+');
    t = t.replace(/[^0-9+]/g, '');
    t = t.replace(/\+/g, '');
    if (hasPlus) t = '+' + t;
    if (t.startsWith('+') && t.length > 16) t = t.slice(0, 16);
    if (!t.startsWith('+') && t.length > 15) t = t.slice(0, 15);
    return t;
  };
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [toast, setToast] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [entries, setEntries] = React.useState([]);
  const [q, setQ] = React.useState('');
  // Entry filter: all | active | noEntries
  const [entryFilter, setEntryFilter] = React.useState('all');
  const [entrySort, setEntrySort] = React.useState('default'); // default | activeFirst | eliminatedFirst
  // User sort: byName (default), byEmail, byCreated
  const [userSort, setUserSort] = React.useState('byCreated');
  // Paid filter: all | paid | unpaid (persist in localStorage)
  const [paidFilter, setPaidFilter] = React.useState(() => {
    try { return localStorage.getItem('adminPaidFilter') || 'all'; } catch { return 'all'; }
  });
  const [userEdits, setUserEdits] = React.useState({}); // userId -> fields
  const [entryEdits, setEntryEdits] = React.useState({}); // entryId -> fields
  const [newEntryName, setNewEntryName] = React.useState({}); // userId -> name
  // Payment controls per entry (mirrors AdminPayments)
  const [pendingType, setPendingType] = React.useState({}); // entryId -> payment_type
  const [pendingDate, setPendingDate] = React.useState({}); // entryId -> local datetime string
  // Create new user inputs (compact form at top of page)
  const [newUser, setNewUser] = React.useState({ first_name: '', last_name: '', email: '', mobile: '', password: '' });
  // Settings: entry fee + entry creation open/close
  const [entryFeeInput, setEntryFeeInput] = React.useState('');
  const [entriesOpen, setEntriesOpen] = React.useState(false);
  // Season lens: year string, "all", or '' until the active season is known
  const [selectedSeason, setSelectedSeason] = useState(() => seasonParamValue(searchParams.get('season')));

  const activeSeason = season.activeSeason;
  const availableSeasons = useMemo(() => {
    const years = new Set(Array.isArray(season.availableSeasons) ? season.availableSeasons : []);
    if (activeSeason !== null) years.add(activeSeason);
    const selected = toSeasonYear(selectedSeason);
    if (selected !== null) years.add(selected);
    return Array.from(years).sort((a, b) => b - a);
  }, [season.availableSeasons, activeSeason, selectedSeason]);

  // Until the active season resolves we cannot prove the selection is live, so default to read-only.
  const seasonResolved = activeSeason !== null;
  const isActiveLens = selectedSeason !== '' && seasonResolved && toSeasonYear(selectedSeason) === activeSeason;
  const isReadOnly = !isActiveLens;
  const seasonQuery = selectedSeason
    ? `?season=${encodeURIComponent(selectedSeason)}`
    : '';
  const seasonLabel = selectedSeason === ALL_SEASONS
    ? 'All seasons'
    : (selectedSeason || (activeSeason !== null ? String(activeSeason) : ''));
  const seasonOptions = [
    ...(activeSeason !== null ? [activeSeason] : []),
    ...availableSeasons.filter(y => y !== activeSeason),
  ];

  // Bulk across ALL users: activate or eliminate every entry (active season only)
  const bulkUpdateAllEntries = async (action) => {
    if (isReadOnly) return;
    if (!['eliminate','activate'].includes(action)) return;
    const verb = action === 'eliminate' ? 'Eliminate ALL entries across all users?' : 'Activate ALL entries across all users?';
    if (!window.confirm(verb)) return;
    try {
      setError(''); setToast('');
      const list = entries.slice();
      let okCount = 0; let failCount = 0;
      // Run in small concurrent batches to avoid flooding
      const batchSize = 10;
      const runBatch = async (slice) => {
        const results = await Promise.allSettled(slice.map(async (e) => {
          const body = action === 'eliminate' ? { is_eliminated: true } : { is_eliminated: false };
          const res = await fetch(`${API_BASE}/api/admin/entries/${encodeURIComponent(e.id)}`,{ method:'PATCH', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, body: JSON.stringify(body) });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data && (data.error || 'Failed'));
          return { id: e.id, data };
        }));
        for (const r of results) {
          if (r.status === 'fulfilled') {
            okCount++;
            const { id, data } = r.value || {};
            if (id != null) {
              // Merge returned fields into local state
              // Prefer a single state update per batch run; accumulate and apply later
              updates.push({ id, data });
            }
          } else {
            failCount++;
          }
        }
      };
      const updates = [];
      for (let i = 0; i < list.length; i += batchSize) {
        // eslint-disable-next-line no-await-in-loop
        await runBatch(list.slice(i, i + batchSize));
      }
      if (updates.length) {
        setEntries(prev => prev.map(e => {
          const u = updates.find(x => x.id === e.id);
          return u ? { ...e, ...u.data } : e;
        }));
      }
      setToast(`Bulk ${action} complete (updated: ${okCount}${failCount ? `, failed: ${failCount}` : ''})`);
    } catch (e) {
      setError(e.message || 'Bulk action failed');
    }
  };

  // Default the lens to the active season once known (unless URL already picked one).
  // If the server has no active season after load, fall back to "all" so the page still works.
  React.useEffect(() => {
    if (selectedSeason) return;
    if (activeSeason !== null) {
      setSelectedSeason(String(activeSeason));
      return;
    }
    if (!season.loading) {
      setSelectedSeason(ALL_SEASONS);
    }
  }, [selectedSeason, activeSeason, season.loading]);

  // Keep ?season= in sync with the selector.
  React.useEffect(() => {
    if (!selectedSeason) return;
    if (searchParams.get('season') !== selectedSeason) {
      const next = new URLSearchParams(searchParams);
      next.set('season', selectedSeason);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason]);

  // Clear entry-side state when the season lens changes so stale rows never render with live controls.
  React.useEffect(() => {
    setEntries([]);
    setEntryEdits({});
    setNewEntryName({});
    setPendingType({});
    setPendingDate({});
  }, [seasonQuery]);

  React.useEffect(() => {
    if (!token) return;
    if (!me) return; // Wait for me to load
    if (!me.is_admin) return; // Don't navigate, just return - the component will handle this with its auth check
    // Wait until we know which season to ask for (or "all"), so we never flash an unscoped list.
    if (!seasonQuery) return;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [uRes, eRes, sRes] = await Promise.all([
          fetch(`${API_BASE}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE}/api/admin/entries${seasonQuery}`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE}/api/admin/settings`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        const uData = await uRes.json().catch(() => []);
        const eData = await eRes.json().catch(() => []);
        const sData = await sRes.json().catch(() => ({}));
        if (!uRes.ok) throw new Error(uData.error || 'Failed to load users');
        if (!eRes.ok) throw new Error(eData.error || 'Failed to load entries');
        if (!sRes.ok) throw new Error(sData.error || 'Failed to load settings');
        setUsers(Array.isArray(uData) ? uData : []);
        setEntries(Array.isArray(eData) ? eData : []);
        try {
          const fee = sData && Object.prototype.hasOwnProperty.call(sData, 'entry_fee') ? sData.entry_fee : null;
          setEntryFeeInput(fee == null ? '' : String(fee));
          setEntriesOpen(!!sData.entries_open);
        } catch {}
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, me, navigate, seasonQuery]);

  // Persist paid filter selection
  React.useEffect(() => {
    try { localStorage.setItem('adminPaidFilter', paidFilter); } catch {}
  }, [paidFilter]);

  // Helper to check if an entry matches the current paid filter
  const matchesPaidFilter = (e) => {
    if (!e) return false;
    if (paidFilter === 'paid') return !!e.is_paid;
    if (paidFilter === 'unpaid') return !e.is_paid;
    return true; // 'all'
  };

  // Compute filtered/sorted user lists BEFORE any early returns to keep hooks order stable
  const filteredUsers = users.filter(u => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const name = `${(u.first_name || '').trim()} ${(u.last_name || '').trim()}`.trim();
    return (
      (name || '').toLowerCase().includes(needle) ||
      (u.email || '').toLowerCase().includes(needle) ||
      String(u.id).includes(needle)
    );
  });
  const usersWithActiveEntry = (u) => entries.some(e => e.user_id === u.id && !e.is_eliminated);
  const usersWithNoEntries = (u) => !entries.some(e => e.user_id === u.id);
  const usersWithPaidMatch = (u) => entries.some(e => e.user_id === u.id && matchesPaidFilter(e));
  const visibleUsers = filteredUsers.filter(u => {
    let entryOk = true;
    if (entryFilter === 'active') entryOk = usersWithActiveEntry(u);
    else if (entryFilter === 'noEntries') entryOk = usersWithNoEntries(u);
    const paidOk = paidFilter === 'all' || usersWithPaidMatch(u);
    return entryOk && paidOk;
  });
  const usersToRender = React.useMemo(() => {
    const arr = visibleUsers.slice();
    if (userSort === 'byEmail') {
      arr.sort((a, b) => (a.email || '').localeCompare(b.email || '') || String(a.id).localeCompare(String(b.id)));
    } else if (userSort === 'byCreated') {
      arr.sort((a, b) => {
        const at = a && a.created_at ? new Date(a.created_at).getTime() : 0;
        const bt = b && b.created_at ? new Date(b.created_at).getTime() : 0;
        return at - bt; // oldest first
      });
    } else if (userSort === 'byName') {
      arr.sort((a, b) => {
        const an = `${(a.first_name || '').trim()} ${(a.last_name || '').trim()}`.trim().toLowerCase();
        const bn = `${(b.first_name || '').trim()} ${(b.last_name || '').trim()}`.trim().toLowerCase();
        const nameCmp = an.localeCompare(bn);
        if (nameCmp !== 0) return nameCmp;
        return (a.email || '').localeCompare(b.email || '');
      });
    }
    return arr;
  }, [visibleUsers, userSort]);

  const adminSaveEntryFee = async () => {
    try {
      setError(''); setToast('');
      const body = {};
      const s = (entryFeeInput || '').trim();
      if (s === '') body.entry_fee = null; else body.entry_fee = parseInt(s, 10);
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save settings');
      const fee = data && Object.prototype.hasOwnProperty.call(data, 'entry_fee') ? data.entry_fee : null;
      setEntryFeeInput(fee == null ? '' : String(fee));
      setToast('Settings saved');
    } catch (e) { setError(e.message); }
  };

  const adminSetEntriesOpen = async (open) => {
    setError(''); setToast('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entries_open: !!open })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update entry creation setting');
      setEntriesOpen(!!data.entries_open);
      setToast(data.entries_open ? 'Entry creation is now open' : 'Entry creation is now closed');
    } catch (e) { setError(e.message); }
  };

  if (!token) return <Navigate to="/auth" replace />;
  if (me == null) return <div className="mx-auto max-w-7xl px-4 py-6"><div>Loading…</div></div>;
  if (me && !me.is_admin) return <div className="mx-auto max-w-7xl px-4 py-6"><h2>Access denied</h2><Link to="/">Return to Dashboard</Link></div>;

  const saveUser = async (userId) => {
    try {
      setError(''); setToast('');
  const bodyRaw = userEdits[userId] || {};
  const body = { ...bodyRaw };
  if (typeof body.mobile === 'string') body.mobile = normalizePhone(body.mobile);
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update user');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...data } : u));
      setUserEdits(prev => ({ ...prev, [userId]: {} }));
      if (me && me.id === userId && typeof setMe === 'function') {
        setMe(prev => (prev ? { ...prev, ...data } : data));
      }
      setToast('User updated');
    } catch (e) { setError(e.message); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their entries and picks? This cannot be undone.')) return;
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete user');
      }
      setUsers(prev => prev.filter(u => u.id !== userId));
      setEntries(prev => prev.filter(e => e.user_id !== userId));
      setToast('User deleted');
    } catch (e) { setError(e.message); }
  };

  const setUserPassword = async (userId, userName) => {
    const raw = window.prompt(`Enter a new password for ${userName || 'this user'}:`);
    if (raw === null) return;
    const password = raw.trim();
    if (!password) return;
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to set password');
      setToast('Password updated');
    } catch (e) { setError(e.message); }
  };

  const createEntryForUser = async (userId) => {
    // POST /api/entries always stamps the active season — never create while viewing another lens.
    if (isReadOnly) {
      setError('Entries are locked for this season.');
      return;
    }
    try {
      setError(''); setToast('');
      const name = (newEntryName[userId] || '').trim();
      if (!name) throw new Error('Enter a name for the new entry');
      const res = await fetch(`${API_BASE}/api/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, name })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Create entry failed');
      setEntries(prev => [...prev, data]);
      setNewEntryName(prev => ({ ...prev, [userId]: '' }));
      setToast('Entry created');
    } catch (e) { setError(e.message); }
  };

  const saveEntry = async (entryId) => {
    if (isReadOnly) {
      setError('Entries are locked for this season.');
      return;
    }
    try {
      setError(''); setToast('');
      const body = { ...(entryEdits[entryId] || {}) };
      if (Object.prototype.hasOwnProperty.call(pendingType, entryId)) {
        body.payment_type = pendingType[entryId] || null;
      }
      const res = await fetch(`${API_BASE}/api/admin/entries/${encodeURIComponent(entryId)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update entry');
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, ...data } : e));
      setEntryEdits(prev => ({ ...prev, [entryId]: {} }));
      setPendingType(prev => {
        if (!Object.prototype.hasOwnProperty.call(prev, entryId)) return prev;
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
      setToast('Entry updated');
    } catch (e) { setError(e.message); }
  };

  const markPaid = async (entryId) => {
    if (isReadOnly) {
      setError('Entries are locked for this season.');
      return;
    }
    try {
      setError(''); setToast('');
      const body = {};
      if (pendingType[entryId]) body.payment_type = pendingType[entryId];
      // Include paid_date when marking paid (backend handles unset/clear)
      const current = entries.find(x => x.id === entryId);
      const currentlyPaid = !!(current && current.is_paid);
      if (!currentlyPaid) {
        const s = pendingDate[entryId];
        if (s && s.trim()) {
          try {
            const d = new Date(s);
            if (!isNaN(d.getTime())) body.paid_date = d.toISOString();
          } catch {}
        } else {
          // If no pending date is set, default to current date/time when marking as paid
          body.paid_date = new Date().toISOString();
        }
      }
      const res = await fetch(`${API_BASE}/api/admin/entries/${encodeURIComponent(entryId)}/payment`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update payment');
      setEntries(prev => prev.map(it => it.id === entryId ? { ...it, is_paid: data.is_paid, payment_type: data.payment_type || it.payment_type, paid_date: data.paid_date } : it));
      setPendingDate(prev => ({ ...prev, [entryId]: '' }));
      setToast('Payment status updated');
    } catch (e) { setError(e.message); }
  };

  const deleteEntry = async (entryId) => {
    if (isReadOnly) {
      setError('Entries are locked for this season.');
      return;
    }
    if (!window.confirm('Delete this entry?')) return;
    try {
      setError(''); setToast('');
      const res = await fetch(`${API_BASE}/api/admin/entries/${encodeURIComponent(entryId)}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete entry');
      }
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setToast('Entry deleted');
    } catch (e) { setError(e.message); }
  };

  const entriesForUser = (userId) => entries.filter(e => e.user_id === userId);
  const sortedEntriesForUser = (userId) => {
    const arr = entriesForUser(userId).filter(matchesPaidFilter).slice();
    if (entrySort === 'activeFirst') {
      arr.sort((a, b) => (a.is_eliminated === b.is_eliminated) ? 0 : (a.is_eliminated ? 1 : -1));
    } else if (entrySort === 'eliminatedFirst') {
      arr.sort((a, b) => (a.is_eliminated === b.is_eliminated) ? 0 : (a.is_eliminated ? -1 : 1));
    }
    return arr;
  };

  const exportAllUsersCsv = () => {
    try {
      const header = [
        'user_id','first_name','last_name','email','mobile','is_admin','user_created_at',
        'entry_id','entry_name','season_year','is_eliminated','is_paid','payment_type','paid_date','entry_created_at'
      ];
      const lines = [header.join(',')];
      for (const u of users) {
        // Export the currently scoped season view (entries already filtered by seasonQuery)
        const es = entries.filter(e => e.user_id === u.id);
        if (!es.length) {
          lines.push([
            csvVal(u.id), csvVal(u.first_name), csvVal(u.last_name), csvVal(u.email), csvVal(u.mobile), csvVal(u.is_admin ? '1' : '0'), csvVal(u.created_at || ''),
            '', '', '', '', '', '', '', ''
          ].join(','));
        } else {
          for (const e of es) {
            lines.push([
              csvVal(u.id), csvVal(u.first_name), csvVal(u.last_name), csvVal(u.email), csvVal(u.mobile), csvVal(u.is_admin ? '1' : '0'), csvVal(u.created_at || ''),
              csvVal(e.id), csvVal(e.name), csvVal(e.season_year != null ? e.season_year : ''), csvVal(e.is_eliminated ? '1' : '0'), csvVal(e.is_paid ? '1' : '0'), csvVal(e.payment_type || ''), csvVal(e.paid_date || ''), csvVal(e.created_at || '')
            ].join(','));
          }
        }
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const seasonSlug = selectedSeason === ALL_SEASONS ? 'all' : (selectedSeason || 'season');
      a.download = `users_and_entries_${seasonSlug}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Failed to export CSV');
    }
  };

  // Export Google Contacts–compatible CSV for all users (exact Google layout requested)
  const exportContactsCsv = () => {
    try {
      // Exact header order per user request (Google Contacts Import Requirements)
      const header = [
        'First Name','Middle Name','Last Name','Phonetic First Name','Phonetic Middle Name','Phonetic Last Name',
        'Name Prefix','Name Suffix','Nickname','File As','Organization Name','Organization Title','Organization Department',
        'Birthday','Notes','Photo','Labels','E-mail 1 - Label','E-mail 1 - Value','Phone 1 - Label','Phone 1 - Value',
        'Address 1 - Label','Address 1 - Formatted','Address 1 - Street','Address 1 - City','Address 1 - PO Box',
        'Address 1 - Region','Address 1 - Postal Code','Address 1 - Country','Address 1 - Extended Address'
      ];

      const escape = (v) => {
        const s = v == null ? '' : String(v);
        if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
        return s;
      };

      const lines = [header.join(',')];

      for (const u of users) {
        const firstName = (u.first_name || '').trim();
        const middleName = '';
        const lastName = (u.last_name || '').trim();
        const phoneticFirst = '';
        const phoneticMiddle = '';
        const phoneticLast = '';
        const namePrefix = '';
        const nameSuffix = '';
        const nickname = (u.nickname || '') || '';
        const fileAs = `${lastName ? lastName + ', ' : ''}${firstName}`.trim();
        const orgName = '';
        const orgTitle = '';
        const orgDept = '';
        const birthday = '';
        const notes = `SBCC USER_ID: ${u.id}`;
        const photo = '';
        const labels = '* myContacts ::: SBCC';
        const email1Label = '* Home';
        const email1Value = u.email || '';
        const phone1Label = 'Mobile';
        const phone1Value = u.mobile || '';
        // Address fields left empty
        const addr1Label = '';
        const addr1Formatted = '';
        const addr1Street = '';
        const addr1City = '';
        const addr1POBox = '';
        const addr1Region = '';
        const addr1Postal = '';
        const addr1Country = '';
        const addr1Extended = '';

        const row = [
          firstName, middleName, lastName, phoneticFirst, phoneticMiddle, phoneticLast,
          namePrefix, nameSuffix, nickname, fileAs, orgName, orgTitle, orgDept,
          birthday, notes, photo, labels, email1Label, email1Value, phone1Label, phone1Value,
          addr1Label, addr1Formatted, addr1Street, addr1City, addr1POBox, addr1Region, addr1Postal, addr1Country, addr1Extended
        ].map(escape);

        lines.push(row.join(','));
      }

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts_google_import.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Failed to export contacts CSV');
    }
  };

  const bulkUpdateEntries = async (userId, action) => {
    if (isReadOnly) return;
    if (!['eliminate','activate'].includes(action)) return;
    if (!window.confirm(`Are you sure you want to ${action} all entries for this user?`)) return;
    try {
      setError(''); setToast('');
      const list = entriesForUser(userId);
      for (const e of list) {
        const body = action === 'eliminate' ? { is_eliminated: true } : { is_eliminated: false };
        const res = await fetch(`${API_BASE}/api/admin/entries/${encodeURIComponent(e.id)}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body)
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setEntries(prev => prev.map(x => x.id === e.id ? { ...x, ...data } : x));
        }
      }
      setToast(`Bulk ${action} complete`);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="admin-users-page mx-auto max-w-7xl space-y-4 px-4 py-6 text-left">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {seasonLabel ? `Admin Users & Entries · ${seasonLabel}` : 'Admin Users & Entries'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Connected to: <code>{API_BASE}</code></p>
        </div>
        {(availableSeasons.length > 0 || selectedSeason === ALL_SEASONS) && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm">
              Season
              <select
                aria-label="Season"
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={selectedSeason}
                onChange={(ev) => setSelectedSeason(ev.target.value)}
              >
                {seasonOptions.map(y => (
                  <option key={y} value={String(y)}>
                    {y === activeSeason ? String(y) : `${y} · archive`}
                  </option>
                ))}
                <option value={ALL_SEASONS}>All seasons</option>
              </select>
            </label>
            {isReadOnly && activeSeason !== null && (
              <button
                type="button"
                onClick={() => setSelectedSeason(String(activeSeason))}
              >
                Back to {activeSeason}
              </button>
            )}
          </div>
        )}
      </div>
      <div style={{ marginBottom: 8 }}>
        <Link to="/admin">← Back to Admin Dashboard</Link>
      </div>
      {isReadOnly && (
        <div
          role="status"
          className="mb-4 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning"
        >
          {selectedSeason === ALL_SEASONS
            ? 'All seasons — read only. Payments and entry changes are season-specific. Switch to '
            : `Archived season — read only. These are the ${seasonLabel} entries and payments. Switch to `}
          {activeSeason !== null ? (
            <button
              type="button"
              style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => setSelectedSeason(String(activeSeason))}
            >
              {activeSeason}
            </button>
          ) : 'the current season'}{' '}
          to record payments or edit entries.
        </div>
      )}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Overview{seasonLabel ? ` · ${seasonLabel}` : ''}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="admin-stat">
              <span className="admin-stat-label">Users</span>
              <span className="admin-stat-value">{Array.isArray(users) ? users.length : 0}</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat-label">Entries</span>
              <span className="admin-stat-value">{Array.isArray(entries) ? entries.length : 0}</span>
            </div>
            <div className="admin-stat">
              <span className="admin-stat-label">Unpaid</span>
              <span className="admin-stat-value">{Array.isArray(entries) ? entries.filter(e => !e.is_paid).length : 0}</span>
            </div>
            {(() => {
              try {
                const fee = (entryFeeInput || '') === '' ? 0 : parseInt(entryFeeInput, 10) || 0;
                const paid = (entries || []).filter(e => !!e.is_paid);
                const count = paid.length;
                const byType = new Map();
                for (const e of paid) {
                  const t = (e.payment_type || 'other').toLowerCase();
                  byType.set(t, (byType.get(t) || 0) + 1);
                }
                const grand = fee * count;
                return (
                  <>
                    <div className="admin-stat">
                      <span className="admin-stat-label">Paid entries</span>
                      <span className="admin-stat-value">{count}</span>
                    </div>
                    <div className="admin-stat">
                      <span className="admin-stat-label">Collected</span>
                      <span className="admin-stat-value">${grand}</span>
                    </div>
                    {Array.from(byType.entries()).map(([t, c]) => (
                      <div key={t} className="admin-stat">
                        <span className="admin-stat-label">{t}</span>
                        <span className="admin-stat-value">{c} (${fee * c})</span>
                      </div>
                    ))}
                  </>
                );
              } catch { return null; }
            })()}
          </div>
          <div className="flex flex-wrap items-center gap-4 border-t border-border pt-4">
            <label className="flex items-center gap-2 text-sm">
              Entry Fee
              <input
                inputMode="numeric"
                value={entryFeeInput}
                onChange={e => setEntryFeeInput(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="e.g., 50"
                className="w-24"
              />
            </label>
            <button type="button" onClick={adminSaveEntryFee}>Save Fee</button>
            <span className="text-muted-foreground opacity-40">|</span>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <strong>Entry Creation:</strong>
              <span>{entriesOpen ? 'Open' : 'Closed'}</span>
              <button type="button" onClick={() => adminSetEntriesOpen(true)} disabled={!token || entriesOpen}>Open</button>
              <button type="button" onClick={() => adminSetEntriesOpen(false)} disabled={!token || !entriesOpen}>Close</button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">When closed, non-admin users cannot create new entries. Admins can still create.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Search &amp; Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            aria-label="Search users"
            placeholder="Search by name, email, or ID"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="min-w-[280px]"
          />
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              Entry filter
              <select aria-label="Entry filter" value={entryFilter} onChange={e => setEntryFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="noEntries">No Entries</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              Payment filter
              <select aria-label="Payment filter" value={paidFilter} onChange={e => setPaidFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              Sort users
              <select value={userSort} onChange={e => setUserSort(e.target.value)}>
                <option value="byName">By name</option>
                <option value="byEmail">By email</option>
                <option value="byCreated">By created date</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              Order
              <select aria-label="Sort users direction" value={userSortDir} onChange={e => setUserSortDir(e.target.value)}>
                <option value="asc">ASC</option>
                <option value="desc">DESC</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              Sort entries
              <select value={entrySort} onChange={e => setEntrySort(e.target.value)}>
                <option value="default">Default</option>
                <option value="activeFirst">Active first</option>
                <option value="eliminatedFirst">Eliminated first</option>
              </select>
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Actions &amp; Export</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={exportAllUsersCsv}>
              {selectedSeason === ALL_SEASONS
                ? 'Export all entries (CSV)'
                : `Export ${seasonLabel || 'season'} entries (CSV)`}
            </button>
            <button type="button" onClick={exportContactsCsv}>Export all user contacts (CSV)</button>
            {!isReadOnly && (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Delete ALL entries? This will keep users but remove all entries and their picks. This cannot be undone. Type the phrase in the next prompt to confirm.')) return;
                    const phrase = window.prompt('Type exactly: DELETE ALL ENTRIES');
                    if (phrase !== 'DELETE ALL ENTRIES') return;
                    try {
                      setError(''); setToast('');
                      const res = await fetch(`${API_BASE}/api/admin/purge/entries-only`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ confirm: 'DELETE ALL ENTRIES' })
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(data.error || 'Purge failed');
                      setEntries([]);
                      setToast(`Purged entries (entries: ${data?.counts?.entries ?? 0}, picks: ${data?.counts?.picks ?? 0})`);
                    } catch (e) { setError(e.message); }
                  }}
                >
                  Delete All Entries
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Delete ALL users and entries? This is destructive and cannot be undone. Type the phrase in the next prompt to confirm.')) return;
                    const phrase = window.prompt('Type exactly: DELETE ALL USERS AND ENTRIES');
                    if (phrase !== 'DELETE ALL USERS AND ENTRIES') return;
                    try {
                      setError(''); setToast('');
                      const res = await fetch(`${API_BASE}/api/admin/purge/users-entries`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ confirm: 'DELETE ALL USERS AND ENTRIES' })
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(data.error || 'Purge failed');
                      setUsers([]);
                      setEntries([]);
                      setToast(`Purged users and entries (users: ${data?.counts?.users ?? 0}, entries: ${data?.counts?.entries ?? 0}, picks: ${data?.counts?.picks ?? 0})`);
                    } catch (e) { setError(e.message); }
                  }}
                >
                  Delete All Users
                </button>
                <button type="button" onClick={() => bulkUpdateAllEntries('activate')}>Activate All Entries</button>
                <button type="button" onClick={() => bulkUpdateAllEntries('eliminate')}>Eliminate All Entries</button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
      {error && <div className="text-destructive">{error}</div>}
      {toast && <div role="status" aria-live="polite" className="text-success">{toast}</div>}
      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <Card className="border-dashed">
            <CardHeader className="pb-2">
              <CardTitle>Create User</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2">
                <input placeholder="First name" value={newUser.first_name} onChange={e => setNewUser(prev => ({ ...prev, first_name: e.target.value }))} />
                <input placeholder="Last name" value={newUser.last_name} onChange={e => setNewUser(prev => ({ ...prev, last_name: e.target.value }))} />
                <input placeholder="Email" value={newUser.email} onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))} className="min-w-[220px]" />
                <input placeholder="Mobile (optional)" value={newUser.mobile} onChange={e => setNewUser(prev => ({ ...prev, mobile: e.target.value }))} />
                <input placeholder="Password (optional)" type="password" value={newUser.password} onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))} />
                <button
                  type="button"
                  disabled={!newUser.first_name.trim() || !newUser.last_name.trim() || !newUser.email.trim()}
                  onClick={async () => {
                    try {
                      setError(''); setToast('');
                      const body = {
                        first_name: (newUser.first_name || '').trim(),
                        last_name: (newUser.last_name || '').trim(),
                        email: (newUser.email || '').trim(),
                      };
                      if (newUser.mobile) body.mobile = normalizePhone(newUser.mobile);
                      if (newUser.password) body.password = newUser.password;
                      const res = await fetch(`${API_BASE}/api/admin/users`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(data.error || 'Failed to create user');
                      const created = data && (data.user || data);
                      if (created && created.id) {
                        setUsers(prev => [...prev, created]);
                        setNewUser({ first_name: '', last_name: '', email: '', mobile: '', password: '' });
                        setToast('User created');
                      }
                    } catch (e) { setError(e.message); }
                  }}
                >Create</button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Users &amp; Entries</CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
          <div className="overflow-auto">
          <table className="admin-users-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>User</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Email / Mobile</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>User Actions</th>
              </tr>
            </thead>
            {(userSortDir === 'desc'
              ? [...(Array.isArray(usersToRender) ? usersToRender : [])].reverse()
              : (Array.isArray(usersToRender) ? usersToRender : [])
            ).map(u => {
                const name = `${(u.first_name || '').trim()} ${(u.last_name || '').trim()}`.trim() || u.email || u.id;
                const ue = userEdits[u.id] || {};
                const es = sortedEntriesForUser(u.id);
                return (
                  <tbody key={u.id} className="admin-user-group">
                    <tr className="admin-user-row">
                      <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <strong>{name}</strong>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
                            <input type="checkbox" checked={!!(ue.is_admin ?? u.is_admin)} onChange={e => setUserEdits(prev => ({ ...prev, [u.id]: { ...(prev[u.id] || {}), is_admin: e.target.checked } }))} /> Admin
                          </label>
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{`ID: ${u.id}`}</div>
                        <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                          <input placeholder="First name" value={ue.first_name ?? (u.first_name || '')} onChange={e => setUserEdits(prev => ({ ...prev, [u.id]: { ...(prev[u.id] || {}), first_name: e.target.value } }))} />
                          <input placeholder="Last name" value={ue.last_name ?? (u.last_name || '')} onChange={e => setUserEdits(prev => ({ ...prev, [u.id]: { ...(prev[u.id] || {}), last_name: e.target.value } }))} />
                        </div>
                      </td>
                      <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px', verticalAlign: 'bottom' }}>
                        <div style={{ display: 'grid', gap: 6 }}>
                          <input placeholder="Email" value={ue.email ?? (u.email || '')} onChange={e => setUserEdits(prev => ({ ...prev, [u.id]: { ...(prev[u.id] || {}), email: e.target.value } }))} style={{ minWidth: 240 }} />
                          <input placeholder="Mobile" value={ue.mobile ?? (u.mobile || '')} onChange={e => setUserEdits(prev => ({ ...prev, [u.id]: { ...(prev[u.id] || {}), mobile: e.target.value } }))} style={{ minWidth: 160 }} />
                        </div>
                      </td>
                      <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                          <button onClick={() => saveUser(u.id)}>Save</button>
                          <button onClick={() => setUserPassword(u.id, name)}>Set Password</button>
                          <button onClick={() => deleteUser(u.id)}>Delete</button>
                        </div>
                        {!isReadOnly && (
                          <>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                              {es.length > 0 ? (
                                <>
                                  <button onClick={() => bulkUpdateEntries(u.id, 'activate')}>Activate All</button>
                                  <button onClick={() => bulkUpdateEntries(u.id, 'eliminate')}>Eliminate All</button>
                                </>
                              ) : (
                                <div style={{ opacity: 0.6 }}>No entries</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input aria-label={`New entry name for user ${u.id}`} placeholder="New entry name" value={newEntryName[u.id] || ''} onChange={e => setNewEntryName(prev => ({ ...prev, [u.id]: e.target.value }))} />
                              <button onClick={() => createEntryForUser(u.id)}>Create Entry</button>
                            </div>
                          </>
                        )}
                        {isReadOnly && es.length === 0 && (
                          <div style={{ opacity: 0.6 }}>No entries</div>
                        )}
                      </td>
                    </tr>
                    {es.length > 0 && (
                      <tr className="admin-entries-wrapper">
                        <td colSpan={3}>
                          <div className="admin-entries-block">
                            <div className="admin-entries-label">Entries ({es.length})</div>
                            <table className="admin-entries-table">
                            <thead>
                              <tr>
                                <th>Entry</th>
                                <th>Payment</th>
                                <th>Paid Date</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {es.map(e => {
                                const ee = entryEdits[e.id] || {};
                                return (
                                  <tr key={e.id} className="admin-entry-row">
                                    <td>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ minWidth: 120 }}><strong>{e.name}</strong></span>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                          {e.is_eliminated ? 'Eliminated' : 'Alive'}
                                        </label>
                                      </div>
                                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>ID: {e.id}</div>
                                      <div style={{ fontSize: 12, opacity: 0.7 }}>Season: {e.season_year != null ? e.season_year : '—'}</div>
                                      {!isReadOnly && (
                                        <div style={{ marginTop: 8 }}>
                                          <input placeholder="New name" value={ee.name ?? ''} onChange={ev => setEntryEdits(prev => ({ ...prev, [e.id]: { ...(prev[e.id] || {}), name: ev.target.value } }))} />
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <div style={{ display: 'grid', gap: 6 }}>
                                        {isReadOnly ? (
                                          <div>{e.payment_type || '—'}</div>
                                        ) : (
                                          <select value={pendingType[e.id] ?? (e.payment_type || '')} onChange={ev => setPendingType(prev => ({ ...prev, [e.id]: ev.target.value }))}>
                                            <option value="">—</option>
                                            <option value="venmo">Venmo</option>
                                            <option value="paypal">Paypal</option>
                                            <option value="square">Square</option>
                                            <option value="cash">Cash</option>
                                          </select>
                                        )}
                                        <div>{e.is_paid ? 'Paid' : 'Unpaid'}</div>
                                      </div>
                                    </td>
                                    <td>
                                      <div style={{ marginBottom: 6 }}>
                                        {e.is_paid || isReadOnly ? (
                                          <div style={{ fontSize: 14, marginBottom: 4 }}>
                                            {e.paid_date ? new Date(e.paid_date).toLocaleString(undefined, { timeZone: 'America/New_York' }) : '—'}
                                          </div>
                                        ) : (
                                          <input
                                            type="datetime-local"
                                            aria-label={`Paid date for ${e.name}`}
                                            value={pendingDate[e.id] ?? (e.paid_date ? formatForDatetimeLocal(e.paid_date) : '')}
                                            onChange={ev => setPendingDate(prev => ({ ...prev, [e.id]: ev.target.value }))}
                                            disabled={!!e.is_paid}
                                          />
                                        )}
                                      </div>
                                      {!isReadOnly && (
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                          <button onClick={() => markPaid(e.id)}>{e.is_paid ? 'Mark Unpaid' : 'Mark Paid'}</button>
                                          {!e.is_paid && <>
                                            <button onClick={() => setPendingDate(prev => ({ ...prev, [e.id]: formatForDatetimeLocal(new Date()) }))}>Now</button>
                                            <button onClick={() => setPendingDate(prev => ({ ...prev, [e.id]: '' }))}>Clear</button>
                                          </>}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      {isReadOnly ? (
                                        <div style={{ opacity: 0.6 }}>Read only</div>
                                      ) : (
                                        <>
                                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                                            <button onClick={() => saveEntry(e.id)}>Save</button>
                                            <button onClick={() => deleteEntry(e.id)}>Delete</button>
                                          </div>
                                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <button onClick={async () => {
                                              try {
                                                setError(''); setToast('');
                                                const body = { is_eliminated: false };
                                                const res = await fetch(`${API_BASE}/api/admin/entries/${encodeURIComponent(e.id)}`, {
                                                  method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body)
                                                });
                                                const data = await res.json().catch(() => ({}));
                                                if (res.ok) {
                                                  setEntries(prev => prev.map(x => x.id === e.id ? { ...x, ...data } : x));
                                                  setToast('Entry activated');
                                                }
                                              } catch (err) { setError(err.message || 'Activate failed'); }
                                            }}>Activate</button>
                                            <button onClick={async () => {
                                              try {
                                                setError(''); setToast('');
                                                const body = { is_eliminated: true };
                                                const res = await fetch(`${API_BASE}/api/admin/entries/${encodeURIComponent(e.id)}`, {
                                                  method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body)
                                                });
                                                const data = await res.json().catch(() => ({}));
                                                if (res.ok) {
                                                  setEntries(prev => prev.map(x => x.id === e.id ? { ...x, ...data } : x));
                                                  setToast('Entry eliminated');
                                                }
                                              } catch (err) { setError(err.message || 'Eliminate failed'); }
                                            }}>Eliminate</button>
                                          </div>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
          </table>
          </div>
            </CardContent>
          </Card>
          {/* Orphan/ungrouped entries (no matching user loaded) for Admin Users & Entries */}
          {(() => {
            const userIds = new Set(users.map(u => u.id));
            const orphans = entries.filter(e => !userIds.has(e.user_id)).filter(matchesPaidFilter);
            if (!orphans.length) return null;
            return (
              <div className="admin-orphans-block">
                <div className="admin-orphans-header">
                  <strong>Ungrouped Entries</strong> <span className="text-muted-foreground">(no matching user loaded)</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>User</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Email</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Entry ID</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Entry</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Eliminated</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Payment</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Payment Type</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Paid Date</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orphans.map(e => (
                      <tr key={e.id}>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{e.user_name || e.user_id || '—'}</td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{e.user_email || '—'}</td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{e.id}</td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{e.name}</td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{e.is_eliminated ? 'Eliminated' : 'Alive'}</td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>{e.is_paid ? 'Paid' : 'Unpaid'}</td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                          {isReadOnly ? (
                            <span>{e.payment_type || '—'}</span>
                          ) : (
                            <select value={pendingType[e.id] ?? (e.payment_type || '')} onChange={ev => setPendingType(prev => ({ ...prev, [e.id]: ev.target.value }))}>
                              <option value="">—</option>
                              <option value="venmo">Venmo</option>
                              <option value="paypal">Paypal</option>
                              <option value="square">Square</option>
                              <option value="cash">Cash</option>
                            </select>
                          )}
                        </td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                          {e.is_paid || isReadOnly ? (
                            <div style={{ fontSize: 14, marginBottom: 4 }}>
                              {e.paid_date ? new Date(e.paid_date).toLocaleString(undefined, { timeZone: 'America/New_York' }) : '—'}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              <input type="datetime-local" aria-label={`Paid date for ${e.name}`} value={pendingDate[e.id] ?? (e.paid_date ? formatForDatetimeLocal(e.paid_date) : '')} onChange={ev => setPendingDate(prev => ({ ...prev, [e.id]: ev.target.value }))} disabled={!!e.is_paid} />
                              <button onClick={() => setPendingDate(prev => ({ ...prev, [e.id]: formatForDatetimeLocal(new Date()) }))}>Now</button>
                              <button onClick={() => setPendingDate(prev => ({ ...prev, [e.id]: '' }))}>Clear</button>
                            </div>
                          )}
                        </td>
                        <td style={{ borderBottom: '1px solid var(--border)', padding: '6px 8px' }}>
                          {isReadOnly ? (
                            <div style={{ opacity: 0.6 }}>Read only</div>
                          ) : (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <button onClick={() => markPaid(e.id)}>{e.is_paid ? 'Mark Unpaid' : 'Mark Paid'}</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
